<template>
  <div class="row items-center justify-between">
    <div>
      <PageHeader breadcrumb="Product" title="Product Analysis" />
    </div>

    <div class="q-gutter-md">
      <Button
        variant="primary"
        label="Export"
        size="lg"
        :disabled="!hasData"
        outline
        @click="exportTable"
      >
        <template v-slot:icon>
          <img :src="getIconUrl('icon_export')" width="30" />
        </template>
      </Button>
      <Button
        variant="primary"
        label="Help"
        size="lg"
        outline
        @click="onToggleModal"
      >
        <template v-slot:icon>
          <img :src="getIconUrl('icon_how_to')" width="30" />
        </template>
      </Button>
    </div>
  </div>

  <PageContent>
    <div class="col q-col-gutter-md">
      <div>
        <FilterCard>
          <q-form ref="filterForm" @submit="onFilter">
            <div class="row items-start q-col-gutter-md">
              <div class="col-xs-12 col-sm-9 col-md-10">
                <div class="row items-start q-col-gutter-md">
                  <div class="col-xs-12 col-sm-6 col-md-2">
                    <FormDate
                      label="Month"
                      mask="####/##"
                      v-model="monthYear"
                      defaultView="YearMonth"
                      :value="monthYear"
                      :rules="[VALIDATION.required]"
                      @input="onSelectDate"
                      tabindex="1"
                    />
                  </div>

                  <div class="col-xs-12 col-sm-6 col-md-2">
                    <FormSelect
                      label="Sort By"
                      v-model="sortBy"
                      :values="sortBy"
                      :options="sortByOptions"
                      :rules="[VALIDATION.required]"
                      tabindex="2"
                    />
                  </div>

                  <div class="col-xs-12 col-sm-6 col-md-2">
                    <FormSelect
                      label="Top"
                      v-model="top"
                      :values="top"
                      :options="topOptions"
                      :rules="[VALIDATION.required]"
                      tabindex="3"
                    />
                  </div>

                  <div class="col-xs-12 col-sm-6 col-md-2">
                    <SelectCategories
                      v-model="category"
                      :values="category"
                      @updateStore="$event => (category = $event)"
                      tabindex="4"
                      withAll
                    />
                  </div>

                  <div class="col-xs-12 col-sm-6 col-md-2">
                    <SelectBrands
                      v-model="brand"
                      :values="brand"
                      @updateStore="$event => (brand = $event)"
                      tabindex="5"
                      withAll
                    />
                  </div>

                  <div class="col-xs-12 col-sm-6 col-md-2">
                    <SelectStores
                      v-model="store"
                      :values="store"
                      :multiple="false"
                      @updateStore="$event => (store = $event)"
                      tabindex="4"
                      withAll
                    />
                  </div>
                </div>
              </div>

              <div class="col-xs-12 col-sm-3 col-md-2 text-right">
                <Button
                  variant="primary"
                  label="Filter"
                  icon="o_filter_alt"
                  :loading="false"
                  :disabled="false"
                  type="submit"
                  tabindex="5"
                />
              </div>
            </div>
          </q-form>
        </FilterCard>
      </div>

      <Loader :loading="!isFiltered && loadingSales" />

      <NoDataCard v-if="noData" />

      <div class="row items-start q-col-gutter-md" v-if="hasData">
        <div class="col-xs-12 col-sm-12 col-md-6">
          <SimpleCard header="Top 20 SKU (Sales)">
            <HorizontalBarChart
              id="barchart"
              axis="xy"
              :limit="20"
              :data="salesData"
            />
          </SimpleCard>
        </div>

        <div class="col-xs-12 col-sm-12 col-md-6">
          <SimpleCard header="Top 20 SKU (Transaction Count)">
            <HorizontalBarChart
              id="barchart2"
              axis="xy"
              :limit="20"
              :data="transCountData"
            />
          </SimpleCard>
        </div>

        <div class="col-xs-12 col-sm-12 col-md-12">
          <Table
            :rows="salesTableData"
            :columns="salesTableHeader"
            :loading="loadingTable"
            :limit="limitPage"
            :page="pageNo"
            :total="totalCount"
            @onRequest="onRequest"
          />
        </div>
      </div>
    </div>
  </PageContent>

  <Modal
    :show="showModal"
    :footer="false"
    header="Help"
    width="700px"
    cancelLabel="Close"
    @close="onToggleModal"
    closeOnly
  >
    <div class="p-4">
      <h5 class="q-mb-md">Product Analysis</h5>
      <p>
        {{ HELP.PRODUCT_ANALYSIS.description }}
      </p>

      <q-separator class="q-my-md" />

      <h6 class="q-mb-md text-sm">Definition of Terms</h6>
      <Table
        style="max-height: 50vh"
        :rows="HELP.PRODUCT_ANALYSIS.terms"
        :columns="HELP.TABLE"
        :limit="0"
        hide-bottom
      >
        <template v-slot:body="props">
          <q-tr>
            <q-td>
              <strong>{{ props.row.name }}</strong>
            </q-td>
            <q-td :style="{ whiteSpace: 'normal' }">
              {{ props.row.description }}
            </q-td>
          </q-tr>
        </template>
      </Table>
    </div>
  </Modal>
</template>

<script>
import { ref, inject, computed, watch, onBeforeMount } from 'vue'
import PageHeader from '@/components/PageHeader'
import PageContent from '@/components/PageContent'
import { SimpleCard, FilterCard, NoDataCard } from '@/components/cards'
import { FormDate, FormSelect } from '@/components/inputs'
import Button from '@/components/Button'
import Table from '@/components/Table'
import Modal from '@/components/Modal'
import { HorizontalBarChart } from '@/components/charts'
import Loader from '@/components/Loader'
import {
  SelectCategories,
  SelectBrands,
  SelectStores
} from '@/components/customs'

import { HELP, optionsTop, optionsSortBy } from '@/constants'

import {
  DATE,
  FORMAT,
  VALIDATION,
  EXPORT,
  Toast,
  getIconUrl,
  fetchData
} from '@/tools'

export default {
  name: 'ProductAnalysis',
  components: {
    PageHeader,
    PageContent,
    SimpleCard,
    FilterCard,
    NoDataCard,
    FormDate,
    FormSelect,
    Button,
    Table,
    Modal,
    HorizontalBarChart,
    Loader,
    SelectCategories,
    SelectBrands,
    SelectStores
  },
  setup() {
    const { showToast } = Toast()
    const state = inject('store')?.state

    const {
      data: dataSales,
      error: errorSales,
      loading: loadingSales,
      post: postSales
    } = fetchData()

    const isRetailer = ref(state?.accountType?.retailer)
    const showModal = ref(false)
    const loadingTable = ref(false)
    const isFiltered = ref(false)
    const limitPage = ref(10)
    const pageNo = ref(1)
    const totalCount = ref(0)
    const filterForm = ref(null)
    const monthYear = ref(
      DATE.toFriendlyDate(
        DATE.toFriendlyFirstDay(DATE.subtractDays(new Date(), 1, 'months'))
      )
    )
    const category = ref('all')
    const brand = ref('all')
    const store = ref('0')
    const top = ref(optionsTop[0].value)
    const sortBy = ref(optionsSortBy[0].value)
    const topOptions = ref(optionsTop)
    const sortByOptions = ref(optionsSortBy)
    const salesTableData = ref(null)
    const salesTableHeader = ref([
      {
        name: 'category',
        label: 'Category Name',
        field: 'category',
        align: 'left',
        sortable: true
      },
      {
        name: 'brand',
        label: 'Brand Name',
        field: 'brand',
        align: 'left',
        sortable: true
      },
      {
        name: 'barcode',
        label: 'Barcode',
        field: 'barcode',
        align: 'center',
        sortable: true
      },
      {
        name: 'product',
        label: 'Product Name',
        field: 'product',
        align: 'left',
        sortable: true
      },
      {
        name: 'sales',
        label: 'Sales',
        field: 'sales',
        format: val => FORMAT.toCurrency(val),
        align: 'center',
        sortable: true
      },
      {
        name: 'salesYTD',
        label: 'Sales YTD',
        field: 'salesYTD',
        format: val => FORMAT.toCurrency(val),
        align: 'center',
        sortable: true
      },
      {
        name: 'quantity',
        label: 'Quantity',
        field: 'quantity',
        format: val => FORMAT.toNumber(val),
        align: 'center',
        sortable: true
      },
      {
        name: 'uniqueCustomers',
        label: 'Unique Customers',
        field: 'uniqueCustomers',
        format: val => FORMAT.toNumber(val),
        align: 'center',
        sortable: true
      },
      {
        name: 'totalCountRank',
        label: 'Total Count Rank (All) ',
        field: 'totalCountRank',
        format: val => FORMAT.toNumber(val),
        align: 'center',
        sortable: true
      },
      {
        name: 'rank',
        label: 'Rank (Cat) ',
        field: 'rank',
        format: val => FORMAT.toNumber(val),
        align: 'center',
        sortable: true
      }
    ])

    const hasData = computed(
      () => salesTableData.value && salesTableData.value.length > 0
    )

    const noData = computed(
      () => salesTableData.value && salesTableData.value.length === 0
    )

    const salesData = ref([])
    const transCountData = ref([])

    const onToggleModal = () => {
      showModal.value = !showModal.value
    }

    const onFilter = () => {
      if (!isFiltered.value) {
        limitPage.value = 10
        pageNo.value = 1
        totalCount.value = 0
      }

      filterForm.value.validate().then(success => {
        if (success) {
          const payload = {
            pagination: {
              page: pageNo.value,
              per_page: limitPage.value
            },
            filters: {
              month_year: DATE.toFriendlyDate(
                DATE.toFriendlyFirstDay(monthYear.value)
              ),
              store: store.value,
              sort_by: sortBy.value,
              limit: top.value
            }
          }

          if (category.value !== 'all') {
            payload.filters.category = category.value
          }

          if (brand.value !== 'all') {
            payload.filters.brand = brand.value
          }

          if (isRetailer.value) {
            payload.filters.principal = state?.defaultID

            postSales('/v1/retailer/product/analysis', payload)
          } else {
            payload.filters.retailer = state?.defaultID

            postSales('/v1/product/analysis', payload)
          }
        } else {
          showToast('Please fill all the required fields', 'info')
        }
      })
    }

    const onSelectDate = data => {
      monthYear.value = data
    }

    const exportTable = () => {
      let barchartImg, barchart2Img

      FORMAT.svg2img('barchart', 600, 350, function (pngData) {
        barchartImg = pngData
      })

      FORMAT.svg2img('barchart2', 600, 350, function (pngData) {
        barchart2Img = pngData
      })

      setTimeout(() => {
        if (barchartImg && barchart2Img) {
          const initialPosition = 10
          const barChartPosition = 20
          const barChart2Position = 20

          EXPORT.exportToExcel({
            header: salesTableHeader.value,
            data: salesTableData.value,
            filename: 'product_analysis',
            chart: barchartImg,
            chart2: barchart2Img,
            chartName: 'Top 20 SKU (Sales)',
            chartName2: 'Top 20 SKU (Transaction Count)',
            position: {
              table: barChartPosition,
              chart: {
                col: initialPosition,
                row: barChartPosition
              },
              chart2: {
                col: barChart2Position,
                row: barChart2Position
              }
            }
          })
        }
      }, 500)
    }

    const onRequest = props => {
      const { page, rowsPerPage } = props.pagination
      isFiltered.value = true
      pageNo.value = page
      limitPage.value = rowsPerPage

      onFilter()
    }

    onBeforeMount(() => {
      watch([dataSales, errorSales, loadingSales], () => {
        if (loadingSales.value) {
          if (isFiltered.value) {
            loadingTable.value = true
          } else {
            salesTableData.value = null
          }
        } else {
          if (errorSales?.value) {
            showToast(
              errorSales?.value?.message ??
                'There was a problem fetching sales.',
              'danger'
            )
          } else if (dataSales?.value) {
            totalCount.value = dataSales?.value?.length

            salesTableData.value = dataSales?.value?.map(item => ({
              category: item?.category_name,
              brand: item?.brand_name,
              barcode: item?.barcode,
              product: item?.sku_desc,
              sales: FORMAT.toFixedNumber(item?.sales ?? 0),
              salesYTD: item?.salesYTD ?? 0,
              quantity: item?.quantity ?? 0,
              uniqueCustomers: item?.customers_p1 ?? 0,
              totalCountRank: item?.rank_all,
              rank: item?.rank_category
            }))

            salesData.value = dataSales?.value
              ?.map(item => ({
                name: item?.sku_desc,
                value: FORMAT.toFixedNumber(item?.sales ?? 0)
              }))
              .sort((a, b) => (a.value > b.value ? -1 : 1))

            transCountData.value = dataSales?.value
              ?.map(item => ({
                name: item?.sku_desc,
                value: item?.trx_count
              }))
              .sort((a, b) => (a.value > b.value ? -1 : 1))
          }

          isFiltered.value = false
          loadingTable.value = false
        }
      })
    })

    return {
      HELP,
      showModal,
      loadingSales,
      loadingTable,
      isFiltered,
      limitPage,
      pageNo,
      totalCount,
      filterForm,
      monthYear,
      category,
      brand,
      store,
      top,
      topOptions,
      sortBy,
      sortByOptions,
      salesTableHeader,
      salesTableData,
      FORMAT,
      VALIDATION,
      getIconUrl,
      hasData,
      noData,
      salesData,
      transCountData,
      onToggleModal,
      onFilter,
      onSelectDate,
      exportTable,
      onRequest
    }
  }
}
</script>
